import { Controller } from "stimulus"
import { getAudioPlayerController } from "utils/audio_player"

export default class extends Controller {
  static targets = [
    "queueHeading", "queueList", "clearButton", "dragHandleTemplate", "playIconTemplate", "removeIconTemplate",
    "nowPlaying", "nowPlayingArtwork", "nowPlayingTitle", "play", "pause",
    "nextFrom", "nextFromLabel", "nextFromArtwork", "nextFromTitle", "nextFromMenu", "nextFromAddToQueueTemplate"
  ]
  static values = { emptyCopy: String }

  connect() {
    this.currentEpisodeId = null

    this.onQueueChanged = this.renderQueue.bind(this)
    window.addEventListener("supercast-queue-changed", this.onQueueChanged)
    this.renderQueue()

    this.onTimeUpdate = this.handleTimeUpdate.bind(this)
    window.addEventListener("supercast-timeupdate", this.onTimeUpdate)

    this.onMetadataBroadcast = this.handleMetadataBroadcast.bind(this)
    window.addEventListener("supercast-episode-metadata", this.onMetadataBroadcast)

    // Deferred: #fixed-player is data-turbo-permanent, but Turbo still detaches/reattaches it
    // during the swap, so media--audio-player's own connect() can still be re-running in this
    // same navigation - reading it synchronously here is a race. Pushing to the next tick lets
    // that controller finish (re)connecting first.
    setTimeout(() => this.syncToCurrentPlayerState(), 0)
  }

  // The audio player may already have an episode loaded (playing or paused) from before this
  // page was visited. Sync once so Now Playing/Next From reflect reality immediately instead of
  // waiting for the next native playing/pause/timeupdate event.
  syncToCurrentPlayerState() {
    const audioPlayer = getAudioPlayerController(this.application)
    if (!audioPlayer) return

    this.applyPlayerState({
      itemId: audioPlayer.itemIdValue,
      itemType: audioPlayer.itemTypeValue,
      mediaType: "audio",
      isPlaying: !audioPlayer.player.paused
    })
  }

  disconnect() {
    window.removeEventListener("supercast-queue-changed", this.onQueueChanged)
    window.removeEventListener("supercast-timeupdate", this.onTimeUpdate)
    window.removeEventListener("supercast-episode-metadata", this.onMetadataBroadcast)
  }

  async renderQueue() {
    const queue = await window.queueDb.getQueue()
    this.queueHeadingTarget.textContent = `Next in Queue · ${queue.length}`
    this.clearButtonTarget.disabled = queue.length === 0
    this.queueListTarget.innerHTML = queue.length
      ? queue.map((item, i) => this.rowHtml(item, i)).join("")
      : `<p class="text-sm text-gray-400">${this.emptyCopyValue}</p>`
  }

  rowHtml(item, index) {
    return `
      <div id="queue-item-${item.id}" data-sortable-item class="flex items-center gap-3 py-2" data-index="${index}">
        <span class="cursor-grab touch-none select-none [-webkit-touch-callout:none]" data-drag-handle>${this.dragHandleTemplateTarget.innerHTML}</span>
        <div class="group flex flex-1 min-w-0 items-center gap-3 cursor-pointer"
             role="button" tabindex="0"
             data-index="${index}"
             data-action="click->queue-view#playItem keydown.enter->queue-view#playItem keydown.space->queue-view#playItem:prevent">
          <div class="relative shrink-0 w-10 h-10">
            ${item.artworkUrl ? `<img src="${item.artworkUrl}" class="w-10 h-10 rounded shrink-0" />` : ""}
            <span class="absolute inset-0 flex items-center justify-center rounded bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity">
              ${this.playIconTemplateTarget.innerHTML}
            </span>
          </div>
          <div class="flex-1 min-w-0 flex items-center">
            <p class="truncate text-sm font-medium m-0">${item.title ?? ""}</p>
          </div>
        </div>
        <span data-index="${index}">${this.removeIconTemplateTarget.innerHTML}</span>
      </div>`
  }

  async reorder(event) {
    const { fromIndex, toIndex } = event.detail
    if (fromIndex === toIndex || fromIndex < 0 || toIndex < 0) return
    await window.queueDb.reorderQueue(fromIndex, toIndex)
    window.dispatchEvent(new CustomEvent("supercast-queue-changed"))
  }

  async remove(event) {
    const index = Number(event.target.closest("[data-index]").dataset.index)
    await window.queueDb.removeFromQueueAt(index)
    window.dispatchEvent(new CustomEvent("supercast-queue-changed"))
  }

  async playItem(event) {
    const index = Number(event.target.closest("[data-index]").dataset.index)
    const queue = await window.queueDb.getQueue()
    const item = queue[index]
    if (!item) return

    const audioPlayer = getAudioPlayerController(this.application)
    if (!audioPlayer) return

    audioPlayer.loadEpisode({
      itemId: item.itemId,
      itemType: item.itemType,
      audioUrl: item.audioUrl,
      timestamp: 0,
      mediaMetadata: {
        title: item.title,
        artist: item.showName,
        artworkUrl: item.artworkUrl
      }
    })

    await window.queueDb.removeFromQueueUpTo(queue, index)
    window.dispatchEvent(new CustomEvent("supercast-queue-changed"))
  }

  async clear() {
    await window.queueDb.clearQueue()
    window.dispatchEvent(new CustomEvent("supercast-queue-changed"))
  }

  // "Now Playing" has no server endpoint of its own - it's driven entirely by the same
  // supercast-timeupdate events the fixed player and play buttons already listen to (plus the
  // initial sync in syncToCurrentPlayerState).
  handleTimeUpdate(event) {
    this.applyPlayerState(event.detail)
  }

  applyPlayerState({ itemId, itemType, mediaType, isPlaying }) {
    if (mediaType !== "audio" || itemType !== "Episode" || !itemId) {
      this.hideNowPlaying()
      return
    }

    this.updatePlayButton(isPlaying)

    if (this.currentEpisodeId == itemId) {
      return
    }

    this.currentEpisodeId = itemId
    this.loadNowPlaying(itemId)
  }

  updatePlayButton(isPlaying) {
    if (this.hasPauseTarget) { this.pauseTarget.hidden = !isPlaying }
    if (this.hasPlayTarget) { this.playTarget.hidden = isPlaying }
  }

  async loadNowPlaying(episodeId) {
    const broadcast = await this.waitForBroadcast(episodeId, 400)
    if (episodeId != this.currentEpisodeId) return
    if (broadcast) {
      this.renderNowPlaying(broadcast.episode)
      this.renderNextFrom(broadcast.nextEpisode, broadcast.playlistName)
      return
    }

    const response = await fetch(`/subscriber_v2/episodes/${episodeId}/metadata`, {
      headers: { Accept: "application/json" }
    })

    if (episodeId != this.currentEpisodeId) {
      return
    }

    if (!response.ok) {
      this.hideNowPlaying()
      return
    }

    const { episode, nextEpisode, playlistName } = await response.json()
    if (episodeId != this.currentEpisodeId) {
      return
    }
    this.renderNowPlaying(episode)
    this.renderNextFrom(nextEpisode, playlistName)
  }

  handleMetadataBroadcast(event) {
    const data = event.detail
    if (data?.episode?.itemId == this.currentEpisodeId) {
      this._resolveBroadcast?.(data)
    }
  }

  waitForBroadcast(episodeId, timeoutMs) {
    return new Promise((resolve) => {
      this._resolveBroadcast = (data) => { clearTimeout(timer); this._resolveBroadcast = null; resolve(data) }
      const timer = setTimeout(() => { this._resolveBroadcast = null; resolve(null) }, timeoutMs)
    })
  }

  renderNowPlaying(episode) {
    if (!episode) {
      this.hideNowPlaying()
      return
    }

    this.nowPlayingArtworkTarget.src = episode.artworkUrl ?? ""
    this.nowPlayingArtworkTarget.classList.toggle("hidden", !episode.artworkUrl)
    this.nowPlayingTitleTarget.textContent = episode.title ?? ""
    this.nowPlayingTarget.classList.remove("hidden")
  }

  hideNowPlaying() {
    if (this.currentEpisodeId == null) {
      return
    }

    this.currentEpisodeId = null
    this.nowPlayingTarget.classList.add("hidden")
    this.nextFromTarget.classList.add("hidden")
  }

  pause(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    const audioPlayer = getAudioPlayerController(this.application)
    audioPlayer?.pause()
  }

  play(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
    }

    const audioPlayer = getAudioPlayerController(this.application)
    audioPlayer?.play()
  }

  renderNextFrom(nextEpisode, playlistName) {
    if (!nextEpisode) {
      this.nextFromTarget.classList.add("hidden")
      return
    }

    this.nextFromLabelTarget.textContent = `Next From · ${playlistName ?? nextEpisode.showName ?? ""}`
    this.nextFromArtworkTarget.src = nextEpisode.artworkUrl ?? ""
    this.nextFromArtworkTarget.classList.toggle("hidden", !nextEpisode.artworkUrl)
    this.nextFromTitleTarget.textContent = nextEpisode.title ?? ""
    this.nextFromMenuTarget.innerHTML = ""
    if (nextEpisode.audioUrl) {
      this.nextFromMenuTarget.appendChild(this.buildAddToQueueButton(nextEpisode))
    }

    this.nextFromTarget.classList.remove("hidden")
  }

  buildAddToQueueButton(nextEpisode) {
    const buttonNode = this.nextFromAddToQueueTemplateTarget.content.cloneNode(true)
    const addButton = buttonNode.querySelector('[data-controller="add-to-queue"]')

    // The component backing this template gates on hotwire_native_app? like every other call site, so
    // inside the native app it renders nothing and there's no button to fill in.
    if (!addButton) {
      return buttonNode
    }

    addButton.setAttribute("data-add-to-queue-item-id-value", nextEpisode.itemId)
    addButton.setAttribute("data-add-to-queue-item-type-value", nextEpisode.itemType)
    addButton.setAttribute("data-add-to-queue-payload-value", JSON.stringify({
      audioUrl: nextEpisode.audioUrl,
      title: nextEpisode.title,
      showName: nextEpisode.showName,
      artworkUrl: nextEpisode.artworkUrl,
      feedItemUrl: nextEpisode.feedItemUrl
    }))

    return buttonNode
  }
}
